/**
 * Overage copy — one place that turns an overage into the words a member reads.
 *
 * Every checkout surface used to hand-roll these sentences in minutes, which is
 * correct for the four minute pools and wrong for the fifth. A session is
 * indivisible, so a session breach reports `coveredMinutes: 0` and the full
 * duration as overage — and the minute-framed copy then told a member who still
 * had hours of daily allowance that their plan "covers 0 min of this booking".
 *
 * Both apps read from here so they cannot drift, and a sixth pool is one branch.
 *
 * @module domains/subscriptions/overageCopy
 */

import type { OverageLimitType } from '@lgc/database/types'
import { formatHoursDisplay } from '@butter/library/shared'

export interface OverageCopyInput {
  limitType: OverageLimitType
  /** Minutes the plan absorbs. Always 0 for a session breach. */
  coveredMinutes: number
  /** Minutes charged at the walk-in rate. The full duration for a session breach. */
  overageMinutes: number
  /** What to call the plan in prose — "membership" or "pass". */
  planNoun?: 'membership' | 'pass'
}

export interface OverageCopy {
  /** Body sentence explaining what is charged and why. */
  explanation: string
  /** Subtitle under the "…Coverage" line item. */
  coveredSubtitle: string
  /** Subtitle under the "Overage Charges" line item. */
  overageSubtitle: string
  /** Value rendered against the coverage row, where a surface shows one. */
  coveredAmountLabel: string
  /**
   * Value for a coverage row that would otherwise read "Free". A session breach
   * covers nothing, and "Free" above "Session limit reached" reads as a
   * contradiction at the moment the member is asked to pay full price.
   */
  coveredValueLabel: string
}

/** One decimal, kept ("1.0 hours"), on the shared display rule. */
const hours = (minutes: number) => formatHoursDisplay(minutes, { unit: '', trimZeros: false })

/**
 * Describe an overage in the unit the breached limit is actually measured in.
 *
 * @example
 * ```ts
 * describeOverage({ limitType: 'daily', coveredMinutes: 60, overageMinutes: 60 })
 * //  → "Your membership covers 60 min of this booking…"
 *
 * describeOverage({ limitType: 'active_sessions', coveredMinutes: 0, overageMinutes: 120 })
 * //  → "You're already holding as many bookings as your membership allows at once…"
 * ```
 */
export function describeOverage({
  limitType,
  coveredMinutes,
  overageMinutes,
  planNoun = 'membership',
}: OverageCopyInput): OverageCopy {
  if (limitType === 'active_sessions') {
    // No partial slot to cover, so nothing here is framed in minutes. The plan
    // still applies to the member's other allowances — this booking simply
    // isn't drawn from the session pool, because the pool is full.
    return {
      explanation:
        `You're already holding as many bookings as your ${planNoun} allows at once. ` +
        `This booking is charged at the standard walk-in rate.`,
      coveredSubtitle: 'Session limit reached',
      overageSubtitle: 'Full booking at walk-in rate',
      coveredAmountLabel: '$0.00',
      coveredValueLabel: '—',
    }
  }

  return {
    explanation:
      `Your ${planNoun} covers ${coveredMinutes} min of this booking. ` +
      `The remaining ${overageMinutes} min will be charged at the standard walk-in rate.`,
    coveredSubtitle: `${coveredMinutes} minutes included`,
    overageSubtitle: `${overageMinutes} minutes at walk-in rate`,
    coveredAmountLabel: `${hours(coveredMinutes)} hours - $0.00`,
    coveredValueLabel: 'Free',
  }
}
